import { useQuery } from '@tanstack/react-query';
import { OtherCityData } from '../types';

// Nominatim API response type
interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  place_rank?: number;
  address_rank?: number;
  address: {
    road?: string;
    house_number?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    country?: string;
  };
}

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org/search';

const mapNominatimToOtherCityData = (result: NominatimResult): OtherCityData => {
  const address = result.address;
  
  // Get city name from various possible fields
  const city = address.city || address.town || address.village || address.municipality || null;
  
  // Build short display name: "Ulica 1, Miasto" or "Dzielnica, Miasto" or just "Miasto"
  const streetPart = address.road 
    ? (address.house_number ? `${address.road} ${address.house_number}` : address.road)
    : address.neighbourhood || address.suburb || null;
  
  const shortDisplayName = streetPart && city 
    ? `${streetPart}, ${city}`
    : city || streetPart || result.display_name;
  
  return {
    lat: result.lat,
    lon: result.lon,
    displayName: shortDisplayName,
    city,
    region: address.county || null,
    state: address.state || null,
    country: address.country || null,
  };
};

export const nominatimKeys = {
  search: (query: string) => ['nominatim', 'search', query] as const,
};

export const useNominatimSearch = (query: string) => {
  return useQuery<OtherCityData[], Error>({
    queryKey: nominatimKeys.search(query),
    queryFn: async () => {
      if (!query || query.length < 2) {
        return [];
      }

      const params = new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: '10',
        featuretype: 'city,town,village',
        countrycodes: 'pl',
        dedupe: '1',
      });

      const response = await fetch(`${NOMINATIM_BASE_URL}?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'TinoMobile/1.0',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to search locations');
      }

      const results: NominatimResult[] = await response.json();
      // Filter results where Address Rank (place_rank or address_rank) is > 0
      const filteredResults = results.filter(
        (result) => (result.place_rank ?? result.address_rank ?? 0) > 0
      );
      return filteredResults.map(mapNominatimToOtherCityData);
    },
    enabled: query.length >= 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });
};

